import { Injectable } from '@angular/core';
import { NgxIndexedDBService, ObjectStoreMeta } from 'ngx-indexed-db';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export type LogLevel = 'INFO' | 'WARN' | 'ERROR';

export interface LogEntry {
  id?: string;
  timeStamp: Date;
  level: LogLevel;
  message: string;
  category?: string;
  context?: any;
  tags?: string[];
}

export interface LogMetadata {
  category?: string;
  context?: any;
  tags?: string[];
}

export interface LogQueryOptions {
  from?: string | Date;
  to?: string | Date;
  category?: string;
  tags?: string[];
  limit?: number;
  order?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class StackLogService {
  private readonly storeName = 'log-store';
  private readonly ensureStorePromise: Promise<void>;
  private readonly storeDefinition: ObjectStoreMeta = {
    store: this.storeName,
    storeConfig: { keyPath: 'id', autoIncrement: false },
    storeSchema: [
      { name: 'timeStamp', keypath: 'timeStamp', options: { unique: false } },
      { name: 'level', keypath: 'level', options: { unique: false } },
      { name: 'category', keypath: 'category', options: { unique: false } }
    ]
  };

  constructor(private readonly db: NgxIndexedDBService) {
    this.ensureStorePromise = this.ensureStore();
  }

  async log(message: string, metadata: LogMetadata = {}): Promise<LogEntry> {
    return this.persist('INFO', message, metadata);
  }

  async warn(message: string, metadata: LogMetadata = {}): Promise<LogEntry> {
    return this.persist('WARN', message, metadata);
  }

  async error(error: Error | string, metadata: LogMetadata = {}): Promise<LogEntry> {
    const resolvedMessage =
      typeof error === 'string'
        ? error
        : error?.message ?? metadata?.context?.message ?? 'Unknown error';
    const mergedContext = {
      ...(metadata?.context ?? {}),
      ...(error instanceof Error
        ? {
            name: error.name,
            stack: error.stack,
          }
        : {})
    };
    return this.persist('ERROR', resolvedMessage, {
      ...metadata,
      context: Object.keys(mergedContext).length > 0 ? mergedContext : undefined
    });
  }

  async queryEntries(options: LogQueryOptions = {}): Promise<LogEntry[]> {
    const fromDate = this.ensureDate(options.from);
    const toDate = this.ensureDate(options.to);
    const categoryFilter = options.category?.trim() ?? null;
    const tagsFilter = Array.isArray(options.tags)
      ? options.tags.map(tag => tag.trim()).filter(tag => tag.length > 0)
      : [];

    if (!this.isIndexedDbAvailable()) {
      console.warn('IndexedDB is not available; log query is skipped.');
      return [];
    }

    await this.ensureStorePromise.catch(() => undefined);

    try {
      const rawRecords = await firstValueFrom(this.db.getAll<any>(this.storeName));
      const normalized = rawRecords.reduce<LogEntry[]>((acc, record) => {
        acc.push(...this.normalizeRecord(record));
        return acc;
      }, []);

      const filtered = normalized.filter(entry => {
        if (!entry.timeStamp) {
          return false;
        }
        if (fromDate && entry.timeStamp < fromDate) {
          return false;
        }
        if (toDate && entry.timeStamp > toDate) {
          return false;
        }
        if (categoryFilter && entry.category !== categoryFilter) {
          return false;
        }
        if (tagsFilter.length > 0) {
          const entryTags = Array.isArray(entry.tags) ? entry.tags : [];
          return tagsFilter.every(tag => entryTags.includes(tag));
        }
        return true;
      });

      filtered.sort((a, b) => a.timeStamp.getTime() - b.timeStamp.getTime());
      if (options.order === 'desc') {
        filtered.reverse();
      }

      if (typeof options.limit === 'number' && options.limit > 0) {
        return filtered.slice(0, options.limit);
      }
      return filtered;
    } catch (error) {
      console.warn('Failed to query log entries.', error);
      return [];
    }
  }

  formatEntries(entries: LogEntry[]): string {
    return entries.map(entry => this.formatEntryLine(entry)).join('\n');
  }

  private async persist(
    level: LogLevel,
    message: string,
    metadata: LogMetadata
  ): Promise<LogEntry> {
    if (!this.isIndexedDbAvailable()) {
      console.warn('IndexedDB is not available; log entry is not persisted.');
      return {
        id: undefined,
        timeStamp: new Date(),
        level,
        message,
        category: metadata?.category ?? 'general',
        context: metadata?.context,
        tags: Array.isArray(metadata?.tags) ? [...metadata.tags] : undefined
      };
    }

    await this.ensureStorePromise.catch(() => undefined);

    const entry: LogEntry = {
      id: uuidv4(),
      timeStamp: new Date(),
      level,
      message,
      category: metadata?.category ?? 'general',
      context: metadata?.context,
      tags: Array.isArray(metadata?.tags) ? [...metadata.tags] : undefined
    };

    try {
      await firstValueFrom(this.db.add(this.storeName, entry));
    } catch (error) {
      console.warn('Failed to persist log entry.', error, entry);
    }
    return entry;
  }

  private normalizeRecord(record: any): LogEntry[] {
    if (!record) {
      return [];
    }

    // 新フォーマット（1 エントリ = 1 レコード）
    if (record.level && record.message && record.timeStamp) {
      const time = this.ensureDate(record.timeStamp);
      if (!time) {
        return [];
      }
      return [
        {
          id: typeof record.id === 'string' ? record.id : undefined,
          timeStamp: time,
          level: (record.level as LogLevel) ?? 'INFO',
          message: String(record.message ?? ''),
          category: typeof record.category === 'string' ? record.category : undefined,
          context: record.context,
          tags: Array.isArray(record.tags) ? record.tags : undefined
        }
      ];
    }

    // 旧フォーマット（logs オブジェクト配下に複数ログを持つ）
    if (record.logs && typeof record.logs === 'object') {
      const entries: LogEntry[] = [];
      const batchTime = this.ensureDate(record.timeStamp);
      const batchId = typeof record.id === 'string' ? record.id : uuidv4();

      Object.keys(record.logs)
        .sort((a, b) => Number(a) - Number(b))
        .forEach(key => {
          const log = record.logs[key];
          if (!log) {
            return;
          }
          const entryTime = this.ensureDate(log.timeStamp) ?? batchTime ?? new Date();
          const message = log.message ?? '';
          const stack = log.stack;
          entries.push({
            id: `${batchId}-${key}`,
            timeStamp: entryTime,
            level: stack ? 'ERROR' as LogLevel : 'INFO',
            message,
            category: record.serialId ? String(record.serialId) : 'batch',
            context: stack ? { stack } : undefined
          });
        });

      return entries;
    }

    return [];
  }

  private async ensureStore(): Promise<void> {
    if (!this.isIndexedDbAvailable()) {
      return;
    }
    try {
      await firstValueFrom(this.db.count(this.storeName));
    } catch (error) {
      if (this.isStoreMissingError(error)) {
        try {
          this.db.createObjectStore(this.storeDefinition);
          await firstValueFrom(this.db.count(this.storeName));
        } catch (creationError) {
          console.warn('Failed to create log store.', creationError);
        }
      } else {
        console.warn('Log store is not accessible.', error);
      }
    }
  }

  private ensureDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private isIndexedDbAvailable(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  private isStoreMissingError(error: unknown): boolean {
    if (!error) {
      return false;
    }
    const name = (error as { name?: string }).name;
    if (name === 'NotFoundError') {
      return true;
    }
    const message = (error as { message?: string }).message ?? '';
    return message.includes('NotFoundError') || message.includes('not found');
  }

  private formatEntryLine(entry: LogEntry): string {
    const timestamp = entry.timeStamp.toISOString();
    const headerParts = [`[${timestamp}]`, `[${entry.level}]`];
    if (entry.category) {
      headerParts.push(`[${entry.category}]`);
    }
    const flattenedMessage = this.flattenWhitespace(entry.message ?? '');
    let line = `${headerParts.join(' ')} ${flattenedMessage}`.trim();

    if (Array.isArray(entry.tags) && entry.tags.length > 0) {
      line += ` | tags=${entry.tags.join(',')}`;
    }

    if (entry.context !== undefined) {
      line += ` | context=${this.stringifyContext(entry.context)}`;
    }

    return line;
  }

  private flattenWhitespace(value: string): string {
    return String(value).replace(/\s+/g, ' ').trim();
  }

  private stringifyContext(context: any): string {
    try {
      return JSON.stringify(context, (_key, val) => {
        if (typeof val === 'string') {
          return this.flattenWhitespace(val);
        }
        return val;
      });
    } catch (_error) {
      return this.flattenWhitespace(String(context));
    }
  }
}
